// Utilidades ANM/REPEM — conversão de coordenadas para DMS (milésimos de segundo),
// geração do CSV no padrão exigido pela ANM e validações da poligonal.
// Datum tratado como SIRGAS 2000 (EPSG:4674), numericamente equivalente ao WGS84.

const pad = (n, l) => String(Math.abs(n)).padStart(l, "0");

// Componentes DMS já formatados como TEXTO (preserva zeros à esquerda/direita).
export function dmsParts(deg, isLat) {
    const sinal = deg < 0 ? "-" : "+";
    let total = Math.round(Math.abs(deg) * 3600 * 1000); // milésimos de segundo de arco
    const milesimos = total % 1000; total = Math.floor(total / 1000);
    const segundos = total % 60; total = Math.floor(total / 60);
    const minutos = total % 60; const graus = Math.floor(total / 60);
    return {
        sinal,
        graus: pad(graus, 3),
        minutos: pad(minutos, 2),
        segundos: pad(segundos, 2),
        milesimos: pad(milesimos, 3),
    };
}

// Exibição legível: -18°36'07.809" (graus sem zero-padding, segundos com 3 milésimos).
export function toDMSDisplay(deg, isLat) {
    let total = Math.round(Math.abs(deg) * 3600 * 1000);
    const mil = total % 1000; total = Math.floor(total / 1000);
    const seg = total % 60; total = Math.floor(total / 60);
    const min = total % 60; const gr = Math.floor(total / 60);
    const sign = deg < 0 ? "-" : "";
    return `${sign}${gr}°${pad(min, 2)}'${pad(seg, 2)}.${pad(mil, 3)}"`;
}

// Chave comparável nível-milésimo (para checar igualdade exata de lat/long entre vértices).
function latKey(lat) { const p = dmsParts(lat, true); return `${p.sinal}${p.graus}${p.minutos}${p.segundos}${p.milesimos}`; }
function lngKey(lng) { const p = dmsParts(lng, false); return `${p.sinal}${p.graus}${p.minutos}${p.segundos}${p.milesimos}`; }

// Normaliza um anel: remove o vértice de fechamento duplicado, se vier repetido.
export function normalizeRing(ring) {
    const pts = ring.map((p) => ({ lat: p.lat, lng: p.lng }));
    if (pts.length >= 2) {
        const a = pts[0], b = pts[pts.length - 1];
        if (Math.abs(a.lat - b.lat) < 1e-9 && Math.abs(a.lng - b.lng) < 1e-9) pts.pop();
    }
    return pts;
}

// Constrói os registros do CSV (V1..Vn + fechamento repetindo V1).
export function buildVertices(ring) {
    const pts = normalizeRing(ring);
    const rows = pts.map((p, i) => ({
        num: i + 1, pa: i === 0, lat: p.lat, lng: p.lng,
        latParts: dmsParts(p.lat, true), lngParts: dmsParts(p.lng, false),
        latDMS: toDMSDisplay(p.lat, true), lngDMS: toDMSDisplay(p.lng, false),
    }));
    if (pts.length) {
        const p = pts[0];
        rows.push({
            num: pts.length + 1, pa: false, closing: true, lat: p.lat, lng: p.lng,
            latParts: dmsParts(p.lat, true), lngParts: dmsParts(p.lng, false),
            latDMS: toDMSDisplay(p.lat, true), lngDMS: toDMSDisplay(p.lng, false),
        });
    }
    return rows;
}

export function repemCSV(ring) {
    const rows = buildVertices(ring);
    const lines = rows.map((r) => [
        r.latParts.sinal, r.latParts.graus, r.latParts.minutos, r.latParts.segundos, r.latParts.milesimos,
        r.lngParts.sinal, r.lngParts.graus, r.lngParts.minutos, r.lngParts.segundos, r.lngParts.milesimos,
    ].join(";"));
    return lines.join("\r\n") + "\r\n";
}

// ---- Geometria ----
function segIntersect(p1, p2, p3, p4) {
    const d = (a, b, c) => (b.lng - a.lng) * (c.lat - a.lat) - (b.lat - a.lat) * (c.lng - a.lng);
    const d1 = d(p3, p4, p1), d2 = d(p3, p4, p2), d3 = d(p1, p2, p3), d4 = d(p1, p2, p4);
    if (((d1 > 0 && d2 < 0) || (d1 < 0 && d2 > 0)) && ((d3 > 0 && d4 < 0) || (d3 < 0 && d4 > 0))) return true;
    return false;
}
export function hasSelfIntersection(ring) {
    const pts = normalizeRing(ring);
    const n = pts.length;
    if (n < 4) return false;
    const closed = [...pts, pts[0]];
    for (let i = 0; i < n; i++) {
        for (let j = i + 1; j < n; j++) {
            if (j === i || j === i + 1 || (i === 0 && j === n - 1)) continue;
            if (segIntersect(closed[i], closed[i + 1], closed[j], closed[j + 1])) return true;
        }
    }
    return false;
}

// Análise dos rumos verdadeiros: cada segmento deve ser N-S (mesma long) ou L-O (mesma lat).
export function segmentAnalysis(ring) {
    const pts = normalizeRing(ring);
    const closed = [...pts, pts[0]];
    const segs = [];
    for (let i = 0; i < pts.length; i++) {
        const a = closed[i], b = closed[i + 1];
        const sameLat = latKey(a.lat) === latKey(b.lat);
        const sameLng = lngKey(a.lng) === lngKey(b.lng);
        segs.push({ from: i + 1, to: (i + 1) % pts.length + 1,
            rumo: sameLat && !sameLng ? "L-O" : sameLng && !sameLat ? "N-S" : (sameLat && sameLng ? "nulo" : "diagonal"),
            ok: (sameLat && !sameLng) || (sameLng && !sameLat) });
    }
    return segs;
}

// Detecta vértices consecutivos duplicados (mesmo ponto nível-milésimo).
export function duplicateVertices(ring) {
    const pts = normalizeRing(ring);
    const dups = [];
    for (let i = 1; i < pts.length; i++) {
        if (latKey(pts[i].lat) === latKey(pts[i - 1].lat) && lngKey(pts[i].lng) === lngKey(pts[i - 1].lng)) dups.push(i + 1);
    }
    return dups;
}

export function validateAnm(ring) {
    const pts = normalizeRing(ring);
    const n = pts.length;
    const records = n ? n + 1 : 0;
    const dups = duplicateVertices(ring);
    const segs = segmentAnalysis(ring);
    const badSegs = segs.filter((s) => !s.ok);
    const selfInt = hasSelfIntersection(ring);
    const checks = [
        { key: "sirgas", label: "SIRGAS 2000 (EPSG:4674)", ok: true },
        { key: "polygon", label: "Polígono válido (≥ 4 vértices)", ok: n >= 4, detail: n < 4 ? `apenas ${n} vértice(s)` : "" },
        { key: "closed", label: "Poligonal fechada (1º = último)", ok: n >= 3 },
        { key: "pa", label: "PA — Ponto de Amarração definido (V1)", ok: n >= 1 },
        { key: "numbering", label: "Numeração sequencial", ok: true },
        { key: "records", label: "≥ 5 registros com fechamento", ok: records >= 5, detail: records < 5 ? `${records} registro(s)` : "" },
        { key: "milesimos", label: "3 casas nos milésimos de segundo", ok: true },
        { key: "dups", label: "Sem vértices duplicados", ok: dups.length === 0, detail: dups.length ? `vértices ${dups.join(", ")}` : "" },
        { key: "selfint", label: "Sem cruzamento de segmentos", ok: !selfInt },
        { key: "rumos", label: "Rumos verdadeiros N-S / L-O", ok: badSegs.length === 0,
          detail: badSegs.length ? `segmento(s) ${badSegs.map((s) => `V${s.from}→V${s.to}`).join(", ")}` : "" },
    ];
    return { ok: checks.every((c) => c.ok), checks, segments: segs, badSegments: badSegs, duplicates: dups, selfIntersect: selfInt, records };
}

// Ajuste "padrão REPEM": força cada segmento ao eixo dominante (iguala lat em L-O, long em N-S).
export function adjustRepem(ring) {
    const pts = normalizeRing(ring).map((p) => ({ lat: p.lat, lng: p.lng }));
    const changes = [];
    const n = pts.length;
    for (let i = 0; i < n; i++) {
        const a = pts[i], b = pts[(i + 1) % n];
        const dLat = Math.abs(b.lat - a.lat), dLng = Math.abs(b.lng - a.lng);
        if (dLat < 1e-12 && dLng < 1e-12) continue;
        if (dLng >= dLat) {
            // L-O: iguala latitude de b à de a
            if (latKey(b.lat) !== latKey(a.lat)) {
                changes.push({ vertice: (i + 1) % n + 1, campo: "Latitude",
                    de: toDMSDisplay(b.lat, true), para: toDMSDisplay(a.lat, true) });
                b.lat = a.lat;
            }
        } else {
            // N-S: iguala longitude de b à de a
            if (lngKey(b.lng) !== lngKey(a.lng)) {
                changes.push({ vertice: (i + 1) % n + 1, campo: "Longitude",
                    de: toDMSDisplay(b.lng, false), para: toDMSDisplay(a.lng, false) });
                b.lng = a.lng;
            }
        }
    }
    return { adjusted: pts, changes };
}

export const PRJ_SIRGAS2000 = 'GEOGCS["SIRGAS 2000",DATUM["Sistema_de_Referencia_Geocentrico_para_las_AmericaS_2000",' +
    'SPHEROID["GRS 1980",6378137,298.257222101]],PRIMEM["Greenwich",0],UNIT["degree",0.0174532925199433],AUTHORITY["EPSG","4674"]]';
